// src/components/onboarding/ExecutiveSummary.tsx
import React from "react";
import { CheckCircle, AlertCircle, AlertTriangle, XCircle } from "lucide-react";

interface ExecutiveSummaryProps {
  performanceDropsCount: number;
  geoTargetingIssuesCount: number;
  contentNetworkIssuesCount: number;
  accountHealthIssuesCount: number;
  searchPartnersIssuesCount?: number;
  budgetConstrainedCount?: number;
  disapprovedAdsCount?: number;
  selectedTab?: string;
  onTabSelect?: (tab: string) => void;
}

const getIssueLevel = (count: number) => {
  if (count === 0) return "good";
  if (count <= 2) return "fair";
  if (count <= 5) return "warning";
  return "critical";
};

const getIssueConfig = (count: number, itemType: string) => {
  const level = getIssueLevel(count);

  switch (level) {
    case "good":
      return {
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        icon: <CheckCircle className="w-5 h-5 text-green-600" />,
        message: "Great job!",
      };
    case "fair":
      return {
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
        message: "Minor issues",
      };
    case "warning":
      return {
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
        icon: <AlertCircle className="w-5 h-5 text-orange-600" />,
        message: "Needs attention",
      };
    case "critical":
      return {
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        icon: <XCircle className="w-5 h-5 text-red-600" />,
        message: "Critical issues",
      };
    default:
      return {
        color: "text-gray-600",
        bgColor: "bg-gray-50",
        borderColor: "border-gray-200",
        icon: <AlertCircle className="w-5 h-5 text-gray-600" />,
        message: "",
      };
  }
};

export const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  performanceDropsCount,
  geoTargetingIssuesCount,
  contentNetworkIssuesCount,
  accountHealthIssuesCount,
  searchPartnersIssuesCount = 0,
  budgetConstrainedCount = 0,
  disapprovedAdsCount = 0,
  selectedTab,
  onTabSelect,
}) => {
  const handleTabClick = (tabId: string) => {
    if (onTabSelect) {
      onTabSelect(tabId);
    }
  };
  // Count the number of issue categories that have problems, not the total number of issues
  const issueCategories = [
    performanceDropsCount > 0 ? 1 : 0,
    geoTargetingIssuesCount > 0 ? 1 : 0,
    contentNetworkIssuesCount > 0 ? 1 : 0,
    accountHealthIssuesCount > 0 ? 1 : 0,
    searchPartnersIssuesCount > 0 ? 1 : 0,
    budgetConstrainedCount > 0 ? 1 : 0,
    disapprovedAdsCount > 0 ? 1 : 0,
  ].reduce((sum, category) => sum + category, 0);

  const totalIssues = issueCategories;

  // Configure each metric
  const performanceConfig = getIssueConfig(
    performanceDropsCount,
    "performance",
  );
  const geoConfig = getIssueConfig(geoTargetingIssuesCount, "geo");
  const networkConfig = getIssueConfig(contentNetworkIssuesCount, "network");
  const healthConfig = getIssueConfig(accountHealthIssuesCount, "health");
  const searchPartnersConfig = getIssueConfig(
    searchPartnersIssuesCount,
    "searchPartners",
  );
  const budgetConfig = getIssueConfig(budgetConstrainedCount, "budget");
  const disapprovedConfig = getIssueConfig(disapprovedAdsCount, "disapproved");

  // Main header based on overall performance
  const getMainHeader = () => {
    if (totalIssues === 0) {
      return {
        title: "🎉 Congratulations! Your Account is Well-Optimized",
        subtitle:
          "We found very few issues with your Google Ads setup. You're doing great!",
        bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
        borderColor: "border-green-200",
      };
    } else if (totalIssues === 1) {
      return {
        title: "✨ Great Job! One Minor Issue Found",
        subtitle:
          "Your account is in excellent shape with just one area for improvement.",
        bgColor: "bg-gradient-to-r from-yellow-50 to-amber-50",
        borderColor: "border-yellow-200",
      };
    } else if (totalIssues <= 3) {
      return {
        title: "✨ Great Job! Minor Optimizations Available",
        subtitle:
          "Your account is in good shape with just a few areas for improvement.",
        bgColor: "bg-gradient-to-r from-yellow-50 to-amber-50",
        borderColor: "border-yellow-200",
      };
    } else if (totalIssues <= 8) {
      return {
        title: "⚡ Optimization Opportunities Found",
        subtitle:
          "Several areas for improvement that could boost your performance.",
        bgColor: "bg-gradient-to-r from-orange-50 to-red-50",
        borderColor: "border-orange-200",
      };
    } else {
      return {
        title: "🚨 Multiple Issues Detected",
        subtitle:
          "Your account needs attention in several areas to improve performance.",
        bgColor: "bg-gradient-to-r from-red-50 to-pink-50",
        borderColor: "border-red-200",
      };
    }
  };

  const header = getMainHeader();

  return (
    <div
      className={`p-6 ${header.bgColor} rounded-lg border ${header.borderColor}`}
    >
      <h2 className="text-lg font-semibold text-slate-800 mb-2">
        {header.title}
      </h2>
      <p className="text-sm text-slate-600 mb-6">{header.subtitle}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Overview Tab - Show all data at once */}
        <div
          className={`p-3 rounded-lg border border-blue-200 bg-blue-50 cursor-pointer hover:opacity-80 transition-all ${selectedTab === "overview" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("overview")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xl font-bold text-blue-600">Overview</div>
            <CheckCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div className="text-sm text-slate-700 font-medium">All Analysis</div>
          <div className="text-xs text-slate-600 mt-1">
            Complete account review
          </div>
          <div className="text-xs font-medium mt-2 text-blue-600">
            View all sections
          </div>
        </div>
        <div
          className={`p-3 rounded-lg border ${performanceConfig.borderColor} ${performanceConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "performance-drops" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("performance-drops")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${performanceConfig.color}`}>
              {performanceDropsCount}
            </div>
            {performanceConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Performance Drops
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {performanceDropsCount === 0
              ? "No declining campaigns"
              : "campaigns declining"}
          </div>
          <div
            className={`text-xs font-medium mt-2 ${performanceConfig.color}`}
          >
            {performanceConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${geoConfig.borderColor} ${geoConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "geo-targeting" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("geo-targeting")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${geoConfig.color}`}>
              {geoTargetingIssuesCount}
            </div>
            {geoConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Geo Targeting
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {geoTargetingIssuesCount === 0
              ? "Settings look good"
              : "potentially wrong settings"}
          </div>
          <div className={`text-xs font-medium mt-2 ${geoConfig.color}`}>
            {geoConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${searchPartnersConfig.borderColor} ${searchPartnersConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "search-partners" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("search-partners")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${searchPartnersConfig.color}`}>
              {searchPartnersIssuesCount}
            </div>
            {searchPartnersConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Search Partners
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {searchPartnersIssuesCount === 0
              ? "Performance acceptable"
              : "performance issues"}
          </div>
          <div
            className={`text-xs font-medium mt-2 ${searchPartnersConfig.color}`}
          >
            {searchPartnersConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${networkConfig.borderColor} ${networkConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "display-network" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("display-network")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${networkConfig.color}`}>
              {contentNetworkIssuesCount}
            </div>
            {networkConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Display Network
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {contentNetworkIssuesCount === 0
              ? "Settings configured well"
              : `check settings in ${contentNetworkIssuesCount} campaigns`}
          </div>
          <div className={`text-xs font-medium mt-2 ${networkConfig.color}`}>
            {networkConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${budgetConfig.borderColor} ${budgetConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "budget" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("budget")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${budgetConfig.color}`}>
              {budgetConstrainedCount}
            </div>
            {budgetConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Budget Limits
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {budgetConstrainedCount === 0
              ? "Budgets optimized"
              : "campaigns need more budget"}
          </div>
          <div className={`text-xs font-medium mt-2 ${budgetConfig.color}`}>
            {budgetConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${disapprovedConfig.borderColor} ${disapprovedConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "disapproved-ads" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("disapproved-ads")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${disapprovedConfig.color}`}>
              {disapprovedAdsCount}
            </div>
            {disapprovedConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Disapproved Ads
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {disapprovedAdsCount === 0 ? "All ads approved" : "ads need fixing"}
          </div>
          <div
            className={`text-xs font-medium mt-2 ${disapprovedConfig.color}`}
          >
            {disapprovedConfig.message}
          </div>
        </div>

        <div
          className={`p-3 rounded-lg border ${healthConfig.borderColor} ${healthConfig.bgColor} cursor-pointer hover:opacity-80 transition-all ${selectedTab === "empty-ad-groups" ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
          onClick={() => handleTabClick("empty-ad-groups")}
        >
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xl font-bold ${healthConfig.color}`}>
              {accountHealthIssuesCount}
            </div>
            {healthConfig.icon}
          </div>
          <div className="text-sm text-slate-700 font-medium">
            Empty Ad Groups
          </div>
          <div className="text-xs text-slate-600 mt-1">
            {accountHealthIssuesCount === 0
              ? "All groups have ads"
              : "groups need ads"}
          </div>
          <div className={`text-xs font-medium mt-2 ${healthConfig.color}`}>
            {healthConfig.message}
          </div>
        </div>
      </div>
    </div>
  );
};
